
import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { useOnboardingState } from "@/integrations/supabase/onboarding";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import Step2NumberSelectExisting from "./Step2NumberSelectExisting";
import Step2NumberBuyNew from "./Step2NumberBuyNew";
import Step2NumberLoading from "./Step2NumberLoading";
import Step2NumberError from "./Step2NumberError";
import Step2Number from "./Step2Number";

export interface NumberOption {
  number_id: string;
  e164: string;
  area_code: string | null;
  status: string;
  country: string;
}

const Step2NumberContainer = ({ onNext, onPrev }: { onNext: () => void; onPrev: () => void }) => {
  const [existingNumbers, setExistingNumbers] = useState<NumberOption[]>([]);
  const [selectedNumberId, setSelectedNumberId] = useState<string | null>(null);
  const [showBuyNew, setShowBuyNew] = useState(false);
  const [areaCode, setAreaCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchingNumbers, setFetchingNumbers] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const { onboarding, buyNumber, syncTwilioNumbers } = useOnboardingState();

  useEffect(() => {
    const fetchNumbers = async () => {
      if (!onboarding) {
        setFetchingNumbers(true);
        return;
      }
      if (!onboarding.tenant_id) {
        setFetchingNumbers(false);
        setFetchError("Unable to determine your account. Please try refreshing the page or going back to the previous step.");
        return;
      }
      setFetchingNumbers(true);
      setFetchError(null);

      try {
        const { data, error } = await supabase
          .from("calie_numbers")
          .select("number_id, e164, area_code, status, country")
          .eq("tenant_id", onboarding.tenant_id)
          .order("created_at", { ascending: true });

        if (error) {
          setExistingNumbers([]);
          setFetchError("Failed to load existing numbers. Please try refreshing the page.");
          toast.error("Failed to load existing numbers", {
            description: "Please try refreshing the page.",
          });
        } else {
          setExistingNumbers(data || []);
          setShowBuyNew((data?.length ?? 0) === 0);
        }
      } catch (err) {
        setFetchError("An unexpected error occurred. Please try again later.");
      } finally {
        setFetchingNumbers(false);
      }
    };

    fetchNumbers();
    setSelectedNumberId(null);
    setAreaCode("");
  }, [onboarding, retryCount]);

  // Number select handler
  const handleNumberSelect = async () => {
    if (!selectedNumberId) {
      setError("Please select a number or choose to buy a new one.");
      return;
    }
    
    setLoading(true);
    try {
      // Update onboarding state to advance to forwarding step
      const { error } = await supabase
        .from("calie_onboarding_state")
        .update({ 
          first_number_added_at: new Date().toISOString(), 
          current_step: "forward" 
        })
        .eq("tenant_id", onboarding?.tenant_id);
      
      if (error) {
        console.error("Failed to update onboarding state:", error);
        setError("Failed to save selection. Please try again.");
        return;
      }
      
      toast.success("Number selected successfully!", {
        description: "You can now proceed to the next step.",
      });
      onNext();
    } catch (err) {
      console.error("Error selecting number:", err);
      setError("Failed to save selection. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Buy number handler
  async function handleBuy(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    if (areaCode.length < 3) {
      setError("Please enter a valid area code");
      setLoading(false);
      return;
    }
    try {
      await buyNumber(areaCode);
      toast.success("New number purchased successfully!", {
        description: "Your new number has been added to your account.",
      });
      onNext();
    } catch (e) {
      setError("Failed to buy number.");
      toast.error("Failed to purchase number", {
        description: "Please try again or contact support.",
      });
    }
    setLoading(false);
  }

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
    setFetchError(null);
    setFetchingNumbers(true);
  };

  const handleManualImport = async () => {
    setFetchingNumbers(true);
    setFetchError(null);
    try {
      if (!onboarding?.tenant_id || !onboarding?.twilio_sid) {
        setFetchError("Twilio account is not configured yet.");
        setFetchingNumbers(false);
        return;
      }
      await syncTwilioNumbers(onboarding.tenant_id, onboarding.twilio_sid);
      toast.success("Numbers imported from Twilio", { description: "Numbers were refreshed!" });
      setRetryCount((c) => c + 1);
    } catch (err: any) {
      setFetchError(err.message || "Could not import numbers from Twilio");
      toast.error("Import failed", { description: err.message });
    } finally {
      setFetchingNumbers(false);
    }
  };

  return (
    <Step2Number
      onboarding={onboarding}
      fetchingNumbers={fetchingNumbers}
      fetchError={fetchError}
      retryCount={retryCount}
      onRetry={handleRetry}
      onPrev={onPrev}
      handleManualImport={handleManualImport}
      existingNumbers={existingNumbers}
      selectedNumberId={selectedNumberId}
      setSelectedNumberId={setSelectedNumberId}
      error={error}
      onNext={onNext}
      handleNumberSelect={handleNumberSelect}
      showBuyNew={showBuyNew}
      setShowBuyNew={setShowBuyNew}
      areaCode={areaCode}
      setAreaCode={setAreaCode}
      handleBuy={handleBuy}
      loading={loading}
      setError={setError}
    />
  );
};

export default Step2NumberContainer;
